import { useState, useEffect } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { Gift } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { readStoredReferral, clearStoredReferral } from '../lib/captureReferral'
import { track } from '../lib/posthog'
import { InAppBrowserBanner } from '../components/auth/InAppBrowserBanner'

export default function SignUp() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const urlRefCode = searchParams.get('ref')

  // Refer code can come from either (a) ?ref=X on this URL directly, or
  // (b) previously captured on a different landing URL (main.tsx → captureReferralFromUrl).
  const refCode = urlRefCode ?? readStoredReferral()

  // Pending household-invite token — if someone clicked /join/:token before signing up,
  // it's stashed in localStorage; surface a banner so they know what's happening.
  const pendingInviteToken = typeof window !== 'undefined'
    ? localStorage.getItem('pending_invite_token')
    : null

  const [displayName,     setDisplayName]     = useState('')
  const [email,           setEmail]           = useState('')
  const [password,        setPassword]        = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading,         setLoading]         = useState(false)
  const [error,           setError]           = useState('')

  // Persist any ?ref= landing on this page so it survives a reload of the form
  useEffect(() => {
    if (urlRefCode) localStorage.setItem('pending_referral_code', urlRefCode)
  }, [urlRefCode])

  const handleSignUp = async () => {
    setError('')

    if (!displayName.trim()) { setError('Please enter your name'); return }
    if (!email.trim())       { setError('Please enter your email'); return }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return }
    if (password !== confirmPassword) { setError('Passwords do not match'); return }

    setLoading(true)

    try {
      const referralCode = localStorage.getItem('pending_referral_code')

      const { data, error: signUpError } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          data: {
            display_name: displayName.trim(),
            referred_by:  referralCode ?? null,
          },
        },
      })

      if (signUpError) { setError(signUpError.message); return }

      if (data.user) {
        if (referralCode) {
          await supabase
            .from('users')
            .update({ referred_by: referralCode })
            .eq('id', data.user.id)
          clearStoredReferral()
        }

        track('user_signed_up', { method: 'email' })

        const pendingToken = localStorage.getItem('pending_invite_token')
        if (pendingToken) {
          localStorage.removeItem('pending_invite_token')
          navigate('/join/' + pendingToken)
        } else {
          navigate('/onboarding')
        }
      }
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-[100dvh] bg-cream flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-[440px]">
        <InAppBrowserBanner />
        <div className="flex justify-center mb-5">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-[16px] bg-forest text-forest-ink font-serif italic text-[32px] font-semibold leading-none">
            P
          </div>
        </div>
        <div className="text-[10px] uppercase tracking-[0.18em] font-medium text-ink-muted mb-2 text-center">
          Sign up
        </div>
        <h1 className="font-serif text-[30px] font-normal leading-[1.05] tracking-[-0.015em] text-ink text-center">
          Join <em className="italic">Pantry</em>.
        </h1>
        <p className="font-serif text-[14px] italic text-ink-muted mt-1 mb-8 leading-[1.4] text-center">
          Free forever. No credit card.
        </p>

        {pendingInviteToken && (
          <div className="mb-6 rounded-[14px] bg-ok-bg border border-forest/15 px-4 py-3 flex items-center gap-3">
            <Gift className="h-[18px] w-[18px] text-forest flex-shrink-0" strokeWidth={1.7} />
            <div className="flex-1 min-w-0">
              <p className="font-serif text-[14px] font-medium text-forest">
                You've been <em className="italic">invited</em>.
              </p>
              <p className="font-serif text-[12px] italic text-forest/80 mt-0.5 leading-[1.4]">
                Sign up to join the household.
              </p>
            </div>
          </div>
        )}

        {refCode && !pendingInviteToken && (
          <div className="mb-6 rounded-[14px] bg-ok-bg border border-forest/15 px-4 py-3 flex items-center gap-3">
            <Gift className="h-[18px] w-[18px] text-forest flex-shrink-0" strokeWidth={1.7} />
            <div className="flex-1 min-w-0">
              <p className="font-serif text-[14px] font-medium text-forest">
                You were <em className="italic">invited</em>.
              </p>
              <p className="font-serif text-[12px] italic text-forest/80 mt-0.5 leading-[1.4]">
                Get a free month of Pantry+ when you subscribe.
              </p>
            </div>
          </div>
        )}

        <form onSubmit={e => { e.preventDefault(); handleSignUp() }} className="flex flex-col gap-4">

          <div className="flex flex-col gap-1.5">
            <label className="text-[12px] font-medium text-ink-muted uppercase tracking-[0.12em]">
              Your name
            </label>
            <input
              type="text"
              placeholder="First and last"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              autoComplete="name"
              className="w-full rounded-[12px] border border-border-sand bg-cream px-4 py-3 text-[15px] text-ink placeholder:text-ink-muted focus:outline-none focus:border-forest focus:ring-2 focus:ring-forest/10 transition"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-[12px] font-medium text-ink-muted uppercase tracking-[0.12em]">
              Email
            </label>
            <input
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              autoComplete="email"
              className="w-full rounded-[12px] border border-border-sand bg-cream px-4 py-3 text-[15px] text-ink placeholder:text-ink-muted focus:outline-none focus:border-forest focus:ring-2 focus:ring-forest/10 transition"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-[12px] font-medium text-ink-muted uppercase tracking-[0.12em]">
              Password
            </label>
            <input
              type="password"
              placeholder="Min. 6 characters"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoComplete="new-password"
              className="w-full rounded-[12px] border border-border-sand bg-cream px-4 py-3 text-[15px] text-ink placeholder:text-ink-muted focus:outline-none focus:border-forest focus:ring-2 focus:ring-forest/10 transition"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-[12px] font-medium text-ink-muted uppercase tracking-[0.12em]">
              Confirm password
            </label>
            <input
              type="password"
              placeholder="Repeat your password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              className="w-full rounded-[12px] border border-border-sand bg-cream px-4 py-3 text-[15px] text-ink placeholder:text-ink-muted focus:outline-none focus:border-forest focus:ring-2 focus:ring-forest/10 transition"
            />
          </div>

          {error && (
            <p className="rounded-[12px] bg-critical-bg border border-critical/20 text-critical text-[13px] p-3">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-[14px] bg-forest text-forest-ink py-3.5 text-[14px] font-bold tracking-[-0.01em] disabled:opacity-50 transition mt-2"
          >
            {loading ? 'Creating account…' : 'Create account'}
          </button>

          <p className="text-[11px] italic font-serif text-ink-muted text-center leading-[1.5]">
            By signing up you agree to our{' '}
            <Link to="/privacy" className="text-forest underline underline-offset-4">Privacy Policy</Link>.
          </p>
        </form>

        <p className="text-[13px] text-ink-muted text-center mt-6">
          Already have an account?{' '}
          <Link to="/sign-in" className="text-forest underline underline-offset-4 font-medium">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  )
}
